import React from "react";
import {
  ColumnDef,
  useReactTable,
  SortingState,
  getCoreRowModel,
  flexRender,
  Row,
  VisibilityState,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useNavigate } from "react-router-dom";
import { Checkbox } from "../ui/checkbox";
import MsgIcon from "/icons/conversation.svg";
import CalendarIcon from "/icons/calendarSession.svg";
import editIcon from "/icons/edit.png";
import trash from "/icons/trash.png";
import { Mentoring } from "@/stores/mentoringStore";

type TableCompProps = {
  data: Mentoring[];
  onDelete?: (id: number) => void;
  onEdit?: (mentoring: Mentoring) => void;
  checkboxColor?: string;
};

const TableComp: React.FC<TableCompProps> = ({ data, onDelete, onEdit }) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [isMobile] = React.useState(window.innerWidth < 768);

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      dateAdded: false,
      lastConnected: false,
    });

  const [selectedRows, setSelectedRows] = React.useState<Set<string>>(
    new Set()
  );
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleResize = () => {
      setColumnVisibility({
        dateAdded: window.innerWidth >= 768,
        lastConnected: window.innerWidth >= 768,
      });
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleAllSelection = (selectAll: boolean) => {
    const newSelectedRows = new Set<string>();
    if (selectAll) {
      data.forEach((row) => newSelectedRows.add(row.mentor_id.toString()));
    }
    setSelectedRows(newSelectedRows);
  };

  const columns = getMentorColumns({
    onEdit,
    onDelete,
    navigate,
    selectedRows,
    setSelectedRows,
    toggleAllSelection,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnVisibility,
    },
  });

  return (
    <div className="w-full h-screen md:h-auto bg-[#F6F8F9] overflow-hidden">
      {isMobile ? (
        <MobileCardView
          data={data}
          onDelete={onDelete}
          onEdit={onEdit}
          navigate={navigate}
        />
      ) : (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="rounded-md border overflow-hidden">
                <div className="min-w-[800px] md:min-w-full h-full flex flex-col">
                  <Table className="w-full flex-1">
                    <TableHeader className="bg-gray-50 sticky top-0 z-10">
                      {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <TableHead
                              key={header.id}
                              className="h-12 px-3 sm:px-4"
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody className="overflow-y-auto flex-1">
                      {table.getRowModel().rows.length ? (
                        table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            className="h-16 hover:bg-slate-200"
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell
                                key={cell.id}
                                className="px-3 sm:px-4 py-3 whitespace-nowrap"
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length}
                            className="text-center text-gray-500 py-8"
                          >
                            Aucun mentorat trouvé.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableComp;

type GetMentorColumnsParams = {
  onEdit?: (mentoring: Mentoring) => void;
  onDelete?: (id: number) => void;
  navigate: (path: string) => void;
  selectedRows: Set<string>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleAllSelection: (selectAll: boolean) => void;
};

export const getMentorColumns = ({
  onEdit,
  onDelete,
  navigate,
  selectedRows,
  setSelectedRows,
  toggleAllSelection,
}: GetMentorColumnsParams): ColumnDef<Mentoring>[] => [
  {
    id: "checkbox",
    header: ({ table }: { table: any }) => (
      <Checkbox
        checked={
          selectedRows.size > 0 &&
          selectedRows.size === table.getRowModel().rows.length
        }
        onCheckedChange={(checked) => toggleAllSelection(checked === true)}
      />
    ),
    cell: ({ row }: { row: Row<Mentoring> }) => (
      <Checkbox
        checked={selectedRows.has(row.original.mentor_id.toString())}
        onCheckedChange={(checked) => {
          const newSelectedRows = new Set(selectedRows);
          if (checked) {
            newSelectedRows.add(row.original.mentor_id.toString());
          } else {
            newSelectedRows.delete(row.original.mentor_id.toString());
          }
          setSelectedRows(newSelectedRows);
        }}
      />
    ),
    size: 50,
  },
  {
    accessorKey: "mentor_name",
    header: "Mentor Name",
  },
  {
    accessorKey: "mentee_name",
    header: "Mentee Name",
  },
  {
    accessorKey: "Start_Date",
    header: "Start Date",
  },
  {
    accessorKey: "End_Date",
    header: "End Date",
  },
  {
    accessorKey: "Status",
    header: "Status",
  },
  {
    id: "actions",
    header: "Action",
    cell: ({ row }: { row: Row<Mentoring> }) => (
      <div className="flex space-x-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate(`/chat?name=${encodeURIComponent(row.original.mentor_id)}`)
          }
        >
          <img src={MsgIcon} alt="message" className="w-5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/calendar/${row.original.mentor_id}`)}
        >
          <img src={CalendarIcon} alt="calendar" className="w-5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit?.(row.original)}
        >
          <img src={editIcon} alt="edit" className="w-5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete?.(row.original.mentor_id)}
        >
          <img src={trash} alt="trash" className="w-5 text-[#FB4F57]" />
        </Button>
      </div>
    ),
    size: 180,
  },
];

type MobileCardViewProps = {
  data: Mentoring[];
  onDelete?: (id: number) => void;
  onEdit?: (mentoring: Mentoring) => void;
  navigate: (path: string) => void;
};

const MobileCardView: React.FC<MobileCardViewProps> = ({
  data,
  onDelete,
  onEdit,
  navigate,
}) => {
  return (
    <div className="p-4 space-y-4">
      {data.map((mentoring) => {
        const startDate = new Date(mentoring.Start_Date).toLocaleDateString(
          "en-US",
          {
            weekday: "short",
            day: "2-digit",
            year: "numeric",
          }
        );
        const endDate = new Date(mentoring.End_Date).toLocaleDateString(
          "en-US",
          {
            weekday: "short",
            day: "2-digit",
            year: "numeric",
          }
        );

        return (
          <div
            key={mentoring.mentor_id}
            className="bg-white shadow rounded p-4"
          >
            <div className="text-sm font-medium text-[#151A20]">
              Mentor: {mentoring.mentor_id}
            </div>
            <div className="mt-2 space-y-1 text-sm">
              <div>
                <strong>Start:</strong> {startDate}
              </div>
              <div>
                <strong>End:</strong> {endDate}
              </div>
              <div>
                <strong>Status:</strong> {mentoring.Status}
              </div>
            </div>
            <div className="mt-4 flex justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate(
                    `/chat?name=${encodeURIComponent(mentoring.mentor_id)}`
                  )
                }
              >
                <img src={MsgIcon} alt="message" className="w-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/calendar/${mentoring.mentor_id}`)}
              >
                <img src={CalendarIcon} alt="calendar" className="w-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit?.(mentoring)}
              >
                <img src={editIcon} alt="edit" className="w-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete?.(mentoring.mentor_id)}
              >
                <img src={trash} alt="trash" className="w-5" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
